import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Users, Plus, Search, Mail, Phone, Building,
  Pencil, Trash2, X, Check, ChevronRight, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { getInitials } from '../lib/utils';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const AVATAR_COLORS = ['#1a73e8','#0f9d58','#e37400','#c5221f','#7b1fa2','#00796b','#c62828','#37474f','#f09300','#006064'];
const avatarColor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) { h = ((h << 5) - h) + name.charCodeAt(i); h |= 0; }
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const ContactAvatar = ({ name, size = 40 }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', background: avatarColor(name || '?'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.38, flexShrink: 0 }}>
    {getInitials(name || '?')}
  </div>
);

// Formulaire ajout/édition inline dans un panneau
const ContactForm = ({ contact, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: contact?.name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    company: contact?.company || '',
    notes: contact?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Nom et email requis'); return; }
    setSaving(true);
    try {
      if (contact) {
        await axios.put(`${API}/contacts/${contact.id}`, form);
        toast.success('Contact modifié');
      } else {
        await axios.post(`${API}/contacts`, form);
        toast.success('Contact ajouté');
      }
      onSave();
    } catch { toast.error('Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const Field = ({ icon: Icon, placeholder, value, onChange, type = 'text' }) => (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border">
      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="flex-1 bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground/60 outline-none border-none"
      />
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
          <X className="w-5 h-5 text-muted-foreground" />
        </button>
        <h2 className="text-[15px] font-semibold text-foreground">
          {contact ? 'Modifier le contact' : 'Nouveau contact'}
        </h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium disabled:opacity-60 transition-opacity"
        >
          <Check className="w-4 h-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      {/* Avatar preview */}
      <div className="flex flex-col items-center py-6 bg-card border-b border-border">
        <ContactAvatar name={form.name || '?'} size={72} />
        <p className="mt-2 text-[14px] font-medium text-foreground">{form.name || 'Nouveau contact'}</p>
      </div>

      {/* Fields */}
      <div className="flex-1 overflow-y-auto">
        <Field icon={Users} placeholder="Nom complet *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
        <Field icon={Mail} placeholder="Email *" value={form.email} onChange={e => setForm({...form, email: e.target.value})} type="email" />
        <Field icon={Phone} placeholder="Téléphone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} type="tel" />
        <Field icon={Building} placeholder="Entreprise" value={form.company} onChange={e => setForm({...form, company: e.target.value})} />
        <div className="flex items-start gap-3 px-4 py-2.5">
          <Pencil className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
          <textarea
            value={form.notes}
            onChange={e => setForm({...form, notes: e.target.value})}
            placeholder="Notes"
            rows={3}
            className="flex-1 bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground/60 outline-none border-none resize-none"
          />
        </div>
      </div>
    </div>
  );
};

// Panneau détail contact
const ContactDetail = ({ contact, onEdit, onDelete, onClose, onCompose }) => (
  <div className="flex flex-col h-full bg-background">
    {/* Header */}
    <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
      <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
        <X className="w-5 h-5 text-muted-foreground" />
      </button>
      <div className="flex items-center gap-1">
        <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-accent transition-colors" title="Modifier">
          <Pencil className="w-4.5 h-4.5 text-muted-foreground" />
        </button>
        <button onClick={() => onDelete(contact.id)} className="p-1.5 rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors" title="Supprimer">
          <Trash2 className="w-4.5 h-4.5 text-muted-foreground" />
        </button>
      </div>
    </div>

    {/* Avatar + nom */}
    <div className="flex flex-col items-center py-8 bg-card border-b border-border">
      <ContactAvatar name={contact.name} size={80} />
      <h2 className="mt-3 text-[20px] font-semibold text-foreground">{contact.name}</h2>
      {contact.company && (
        <p className="text-[13px] text-muted-foreground mt-0.5">{contact.company}</p>
      )}
    </div>

    {/* Actions rapides */}
    <div className="flex justify-center gap-6 py-4 border-b border-border bg-card">
      {contact.email && (
        <button
          onClick={() => onCompose && onCompose(contact.email)}
          className="flex flex-col items-center gap-1.5 px-4 py-2 rounded-xl hover:bg-accent transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
            <Mail className="w-5 h-5 text-primary" />
          </div>
          <span className="text-[11px] text-muted-foreground">Email</span>
        </button>
      )}
      {contact.phone && (
        <a href={`tel:${contact.phone}`} className="flex flex-col items-center gap-1.5 px-4 py-2 rounded-xl hover:bg-accent transition-colors">
          <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center">
            <Phone className="w-5 h-5 text-green-600" />
          </div>
          <span className="text-[11px] text-muted-foreground">Appel</span>
        </a>
      )}
    </div>

    {/* Détails */}
    <div className="flex-1 overflow-y-auto">
      {contact.email && (
        <div className="flex items-center gap-4 px-5 py-3.5 border-b border-border">
          <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-[14px] text-foreground">{contact.email}</p>
            <p className="text-[11px] text-muted-foreground">Email</p>
          </div>
        </div>
      )}
      {contact.phone && (
        <div className="flex items-center gap-4 px-5 py-3.5 border-b border-border">
          <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-[14px] text-foreground">{contact.phone}</p>
            <p className="text-[11px] text-muted-foreground">Téléphone</p>
          </div>
        </div>
      )}
      {contact.company && (
        <div className="flex items-center gap-4 px-5 py-3.5 border-b border-border">
          <Building className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-[14px] text-foreground">{contact.company}</p>
            <p className="text-[11px] text-muted-foreground">Entreprise</p>
          </div>
        </div>
      )}
      {contact.notes && (
        <div className="flex items-start gap-4 px-5 py-3.5 border-b border-border">
          <Pencil className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[14px] text-foreground whitespace-pre-wrap">{contact.notes}</p>
            <p className="text-[11px] text-muted-foreground">Notes</p>
          </div>
        </div>
      )}
    </div>
  </div>
);

export const ContactsView = ({ onCompose }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [panel, setPanel] = useState(null); // 'detail' | 'add' | 'edit'

  const fetchContacts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contacts`);
      setContacts(r.data);
    } catch { console.error('contacts fetch failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success('Contact supprimé');
      if (selectedContact?.id === id) { setSelectedContact(null); setPanel(null); }
      fetchContacts();
    } catch { toast.error('Erreur suppression'); }
  };

  const filtered = contacts.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.company || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Grouper par lettre
  const grouped = filtered.reduce((acc, c) => {
    const letter = c.name[0]?.toUpperCase() || '#';
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(c);
    return acc;
  }, {});
  const letters = Object.keys(grouped).sort();

  const showPanel = panel !== null;

  return (
    <div className="flex h-full bg-background" data-testid="contacts-view">
      {/* ── LISTE ── */}
      <div className={cn(
        "flex flex-col transition-all duration-200",
        showPanel ? "w-full md:w-[360px] md:border-r md:border-border hidden md:flex" : "w-full flex"
      )}>
        {/* Header liste */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card flex-shrink-0">
          <h1 className="text-[16px] font-semibold text-foreground">
            Contacts <span className="text-[13px] font-normal text-muted-foreground ml-1">({contacts.length})</span>
          </h1>
          <button
            onClick={() => { setSelectedContact(null); setPanel('add'); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium hover:bg-primary/90 transition-colors"
            data-testid="add-contact-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Ajouter</span>
          </button>
        </div>

        {/* Barre de recherche */}
        <div className="px-4 py-2.5 border-b border-border bg-card flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              className="w-full h-9 pl-9 pr-4 rounded-lg bg-muted text-[13px] text-foreground placeholder:text-muted-foreground outline-none border border-transparent focus:border-primary/30 transition-colors"
              data-testid="contact-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-accent">
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-border animate-pulse">
                <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 bg-muted rounded w-2/5" />
                  <div className="h-3 bg-muted rounded w-3/5" />
                </div>
              </div>
            ))
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <UserPlus className="w-12 h-12 mb-3 text-muted-foreground/30" />
              <p className="text-[14px] font-medium text-muted-foreground">Aucun contact</p>
              {!searchQuery && (
                <button
                  onClick={() => { setSelectedContact(null); setPanel('add'); }}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium"
                >
                  <Plus className="w-4 h-4" />Ajouter un contact
                </button>
              )}
            </div>
          ) : letters.map(letter => (
            <div key={letter}>
              {/* Séparateur alphabétique */}
              <div className="px-4 py-1.5 bg-muted/30 border-b border-border">
                <span className="text-[12px] font-semibold text-muted-foreground">{letter}</span>
              </div>
              {grouped[letter].map(contact => {
                const isActive = selectedContact?.id === contact.id;
                return (
                  <button
                    key={contact.id}
                    onClick={() => { setSelectedContact(contact); setPanel('detail'); }}
                    data-testid={`contact-item-${contact.id}`}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 border-b border-border transition-colors text-left hover:bg-accent/50",
                      isActive && "bg-primary/10"
                    )}
                  >
                    <ContactAvatar name={contact.name} size={40} />
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-[14px] font-medium truncate", isActive ? "text-primary" : "text-foreground")}>
                        {contact.name}
                      </p>
                      <p className="text-[12px] text-muted-foreground truncate">
                        {contact.email}
                        {contact.company && <span className="text-muted-foreground/60"> · {contact.company}</span>}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── PANNEAU DÉTAIL / FORMULAIRE ── */}
      {showPanel && (
        <div className="flex-1 min-w-0 flex flex-col border-l border-border md:flex hidden md:block">
          {/* Sur mobile : overlay full screen */}
          <div className="absolute md:relative inset-0 z-30 md:z-auto flex flex-col md:h-full bg-background">
            {panel === 'detail' && selectedContact && (
              <ContactDetail
                contact={selectedContact}
                onEdit={() => setPanel('edit')}
                onDelete={(id) => { handleDelete(id); setPanel(null); }}
                onClose={() => setPanel(null)}
                onCompose={onCompose}
              />
            )}
            {(panel === 'add' || panel === 'edit') && (
              <ContactForm
                contact={panel === 'edit' ? selectedContact : null}
                onSave={() => { fetchContacts(); setPanel(panel === 'edit' ? 'detail' : null); }}
                onCancel={() => setPanel(panel === 'edit' ? 'detail' : null)}
              />
            )}
          </div>
        </div>
      )}

      {/* Mobile : panneau en plein écran par-dessus */}
      {showPanel && (
        <div className="md:hidden fixed inset-0 z-50 bg-background flex flex-col">
          {panel === 'detail' && selectedContact && (
            <ContactDetail
              contact={selectedContact}
              onEdit={() => setPanel('edit')}
              onDelete={(id) => { handleDelete(id); setPanel(null); }}
              onClose={() => setPanel(null)}
              onCompose={onCompose}
            />
          )}
          {(panel === 'add' || panel === 'edit') && (
            <ContactForm
              contact={panel === 'edit' ? selectedContact : null}
              onSave={() => { fetchContacts(); setPanel(panel === 'edit' ? 'detail' : null); }}
              onCancel={() => setPanel(panel === 'edit' ? 'detail' : null)}
            />
          )}
        </div>
      )}
    </div>
  );
};
